import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { ChartLineUp, CheckCircle, LockKey, ArrowsClockwise, Warning, Crown } from "@phosphor-icons/react";

const panel = {
  background: "var(--velox-panel)",
  border: "1px solid var(--velox-border)",
};

export const TradingViewIndicator = () => {
  const [data, setData] = useState(null);
  const [tvId, setTvId] = useState("");
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(false);

  const load = useCallback(() => {
    axios.get(`${AFF_API}/affiliate/tradingview/status`, { headers: getAffHeaders() })
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const activate = async () => {
    if (!tvId.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${AFF_API}/affiliate/tradingview/activate`, { tv_username: tvId.trim() }, { headers: getAffHeaders() });
      toast.success(r.data.message || "TradingView ID activated");
      setTvId("");
      setChanging(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const act = data.activation;
  const changesLeft = act ? act.changes_max - act.changes_used : 3;

  return (
    <div data-testid="tv-indicator-card" className="rounded-2xl p-5 space-y-4" style={panel}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(34,211,238,0.12)" }}>
            <ChartLineUp size={22} style={{ color: "var(--velox-accent-cyan)" }} />
          </div>
          <div>
            <h3 className="font-bold text-base" style={{ color: "var(--velox-text)" }}>TradingView Indicators</h3>
            <p className="text-xs" style={{ color: "var(--velox-muted)" }}>Veloxalgotech-HFT · invite-only script</p>
          </div>
        </div>
        {data.lifetime_offer_enabled && !act && (
          <span data-testid="tv-lifetime-offer-badge" className="text-[11px] font-bold px-3 py-1 rounded-full flex items-center gap-1"
            style={{ background: "rgba(250,204,21,0.14)", color: "#facc15", border: "1px solid rgba(250,204,21,0.35)" }}>
            <Crown size={12} weight="fill" /> LAUNCH OFFER — LIFETIME ACCESS
          </span>
        )}
      </div>

      {!data.fee_paid && (
        <div data-testid="tv-fee-required" className="rounded-xl p-4 text-sm flex items-center gap-2"
          style={{ background: "rgba(251,146,60,0.08)", border: "1px solid rgba(251,146,60,0.3)", color: "#fdba74" }}>
          <Warning size={18} /> Pay the ₹2,999 platform fee to unlock your TradingView indicator access.
        </div>
      )}

      {data.fee_paid && !act && (
        <div className="space-y-3">
          <p className="text-sm" style={{ color: "var(--velox-muted)" }}>
            Activate your TradingView indicators — enter your TradingView username <b>once</b>. Access is granted to this exact ID
            {data.lifetime_offer_enabled ? " with LIFETIME validity (launch offer)." : " for 1 year."} You can change it a maximum of <b>3 times</b>.
          </p>
          <div className="flex gap-2 flex-wrap">
            <input
              data-testid="tv-username-input"
              value={tvId}
              onChange={e => setTvId(e.target.value)}
              placeholder="Your TradingView username"
              className="flex-1 min-w-[220px] px-4 py-2.5 rounded-xl text-sm outline-none"
              style={{ background: "var(--velox-bg)", border: "1px solid var(--velox-border)", color: "var(--velox-text)" }}
            />
            <button
              data-testid="tv-activate-btn"
              onClick={activate}
              disabled={busy || !tvId.trim()}
              className="px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-50 transition-opacity"
              style={{ background: "var(--velox-accent-cyan)", color: "#04121a" }}
            >
              {busy ? "Activating..." : "Activate"}
            </button>
          </div>
        </div>
      )}

      {act && (
        <div className="space-y-3">
          <div className="rounded-xl p-4 flex items-center justify-between flex-wrap gap-3"
            style={{ background: act.expired ? "rgba(244,63,94,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${act.expired ? "rgba(244,63,94,0.3)" : "rgba(34,197,94,0.3)"}` }}>
            <div className="flex items-center gap-3">
              <CheckCircle size={22} weight="fill" style={{ color: act.expired ? "#fb7185" : "#4ade80" }} />
              <div>
                <p data-testid="tv-active-username" className="font-bold text-sm" style={{ color: "var(--velox-text)" }}>{act.tv_username}</p>
                <p className="text-xs" style={{ color: "var(--velox-muted)" }}>
                  Activated {act.activated_at?.slice(0, 10)}
                </p>
              </div>
            </div>
            <span data-testid="tv-validity-badge" className="text-[11px] font-bold px-3 py-1 rounded-full"
              style={act.expired
                ? { background: "rgba(244,63,94,0.15)", color: "#fb7185" }
                : act.lifetime
                  ? { background: "rgba(250,204,21,0.14)", color: "#facc15" }
                  : { background: "rgba(34,211,238,0.12)", color: "var(--velox-accent-cyan)" }}>
              {act.expired ? "EXPIRED" : act.lifetime ? "LIFETIME ACCESS" : `VALID TILL ${act.expires_at?.slice(0, 10)}`}
            </span>
          </div>

          {data.indicator_url && !act.expired && (
            <div className="space-y-1.5">
              {act.grant_status === "granted" ? (
                <p data-testid="tv-grant-live-note" className="text-xs" style={{ color: "#4ade80" }}>
                  ✓ Access is LIVE on TradingView — open the chart → Indicators → <b>Invite-only scripts</b> → Veloxalgotech-HFT.
                </p>
              ) : (
                <p data-testid="tv-grant-pending-note" className="text-xs" style={{ color: "#fdba74" }}>
                  Access is being enabled on TradingView — it will appear in your Invite-only scripts shortly.
                </p>
              )}
              <a data-testid="tv-open-indicator-link" href={data.indicator_url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold hover:underline"
                style={{ color: "var(--velox-accent-cyan)" }}>
                <ChartLineUp size={16} /> Open Veloxalgotech-HFT on TradingView →
              </a>
            </div>
          )}

          <div className="pt-2 border-t" style={{ borderColor: "var(--velox-border)" }}>
            {act.locked ? (
              <p data-testid="tv-locked-note" className="text-xs flex items-center gap-2" style={{ color: "#fb7185" }}>
                <LockKey size={14} /> TradingView ID locked — all 3 changes used. Contact support to reset.
              </p>
            ) : !changing ? (
              <button data-testid="tv-change-id-btn" onClick={() => setChanging(true)}
                className="text-xs font-semibold flex items-center gap-1.5 hover:underline" style={{ color: "var(--velox-muted)" }}>
                <ArrowsClockwise size={14} /> Change TradingView ID ({changesLeft} change{changesLeft !== 1 ? "s" : ""} remaining)
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs" style={{ color: "#fdba74" }}>
                  <Warning size={12} className="inline mr-1" />
                  This will remove access from <b>{act.tv_username}</b> and grant it to the new ID. {changesLeft} change{changesLeft !== 1 ? "s" : ""} remaining — after that the ID is locked forever.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <input
                    data-testid="tv-new-username-input"
                    value={tvId}
                    onChange={e => setTvId(e.target.value)}
                    placeholder="New TradingView username"
                    className="flex-1 min-w-[200px] px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ background: "var(--velox-bg)", border: "1px solid var(--velox-border)", color: "var(--velox-text)" }}
                  />
                  <button data-testid="tv-confirm-change-btn" onClick={activate} disabled={busy || !tvId.trim()}
                    className="px-4 py-2 rounded-lg text-xs font-bold disabled:opacity-50"
                    style={{ background: "rgba(251,146,60,0.9)", color: "#1a0d02" }}>
                    {busy ? "Updating..." : "Confirm Change"}
                  </button>
                  <button data-testid="tv-cancel-change-btn" onClick={() => { setChanging(false); setTvId(""); }}
                    className="px-4 py-2 rounded-lg text-xs font-semibold"
                    style={{ background: "var(--velox-bg)", border: "1px solid var(--velox-border)", color: "var(--velox-muted)" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TradingViewIndicator;
